/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import Mousetrap from 'mousetrap';
import _ from 'lodash';
import clsx from 'clsx';

import { MountPoints } from '../constants/portalLocationConstants';
import { ifClickable } from '../utils/touchUtils';
import BaseComponent from './BaseComponent';
import IconExpand from '../icons/IconExpand';
import ModalModifierClassMapping from '../constants/ModalModifierClassMapping';
import Portal from './Portal';

interface Props {
  children?: React.ReactNode;
  className?: string;
  containerComponent: any;
  containerProps?: {
    onClick(...args: unknown[]): void;
    [propName: string]: unknown;
  };
  disableBackgroundClose?: boolean;
  isOpen?: boolean;
  isMinimized?: boolean;
  modifiers?: string[];
  mountLocation?: MountPoints;
  onMaximize?(event: React.MouseEvent<HTMLElement>): void;
  onClose(evt: KeyboardEvent | React.SyntheticEvent): void;
  'data-testid'?: string;
}

export default class ModalWrapper extends BaseComponent<Props> {
  static defaultProps = {
    containerComponent: 'div',
    disableBackgroundClose: true,
    modifiers: [],
  };

  componentDidMount() {
    const { isOpen } = this.props;
    // only bind if it's open
    if (isOpen) {
      Mousetrap.bind('esc', this.handleClose);
      this.disableBodyScroll();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isOpen && this.props.isOpen) {
      Mousetrap.bind('esc', this.handleClose);
      this.disableBodyScroll();
    }
  }

  componentWillUnmount() {
    Mousetrap.unbind('esc');
    document.body.classList.remove('Body--no-scroll');
  }

  disableBodyScroll() {
    // We don't want a closed modal to prevent the user from scrolling the page.
    document.body.classList.add('Body--no-scroll');
  }

  handleClose(event: KeyboardEvent) {
    const { onClose } = this.props;
    if (onClose) {
      onClose(event);
    }
  }

  handleClickModalContainer(event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation();
  }

  handleBackgroundClick(event: React.MouseEvent<HTMLElement>) {
    const { onClose, disableBackgroundClose } = this.props;
    if (onClose && !disableBackgroundClose) {
      onClose(event);
    }
  }

  handleMinimizedBlockerClick(event: React.MouseEvent<HTMLElement>) {
    const { onMaximize } = this.props;
    if (onMaximize) {
      onMaximize(event);
    }
  }

  render() {
    const {
      children,
      className,
      containerComponent,
      containerProps,
      isMinimized,
      isOpen,
      modifiers,
      mountLocation,
    } = this.props;

    const modifierClasses = _.map(modifiers, (modifier: string) => {
      return ModalModifierClassMapping[modifier];
    });

    const finalClassName = clsx('NewModal', className, modifierClasses);
    const actualContainerProps = containerProps || {
      className: 'NewModal-container',
      onClick: _.noop,
    };

    actualContainerProps.onClick = ifClickable(this.handleClickModalContainer);

    const container = React.createElement(containerComponent, actualContainerProps, children);

    // this was for backwards compatibility with other modals
    const portalIsOpen = !_.isNil(isOpen) ? isOpen : true;

    return (
      <Portal isOpen={portalIsOpen} mountLocation={mountLocation}>
        <div className={finalClassName} data-testid={this.props['data-testid']}>
          {/* blocks the content */}
          {isMinimized && (
            <div
              className="ComposableModal-content--minimized"
              onClick={this.handleMinimizedBlockerClick}
            >
              <IconExpand />
            </div>
          )}
          <div className="NewModal-overlay" onClick={this.handleBackgroundClick}>
            {container}
          </div>
        </div>
      </Portal>
    );
  }
}
